import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getCurrentUser, signOut } from '../../services/authService';
import { supabase } from '../../services/supabaseClient';
import './Header.css';

/**
 * Header component for the application
 * Displays navigation and user authentication status
 */
const Header = () => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchUser = async () => {
      const { user } = await getCurrentUser();
      setUser(user);
      
      if (user) {
        // Fetch the user's profile to get their name, user type, venue name, and profile image
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name, first_name, last_name, user_type, profile_image_url, venue_name')
          .eq('uuid', user.id)
          .single();
        
        if (!error && data) {
          setProfile(data);
          console.log('Fetched profile with image:', data.profile_image_url);
        }
      }
      
      setLoading(false);
    };

    fetchUser();
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    setUser(null);
    navigate('/login');
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <header className="header">
      <div className="container header-container">
        <div className="logo">
          <Link to="/">
            <h1><i className="bi bi-music-note-beamed"></i> Unissn</h1>
          </Link>
        </div>

        <button className="menu-toggle" onClick={toggleMenu}>
          <span className="menu-icon"></span>
        </button>

        <nav className={`nav ${menuOpen ? 'open' : ''}`}>
          {!loading && user ? (
            <>
              <ul className="nav-list">
                <li className="nav-item">
                  <Link to="/" className="nav-link"><i className="bi bi-speedometer2"></i> Dashboard</Link>
                </li>
                <li className="nav-item">
                  <Link to="/gigs" className="nav-link"><i className="bi bi-calendar-event"></i> Gigs</Link>
                </li>
                <li className="nav-item">
                  <Link to="/artists" className="nav-link"><i className="bi bi-people"></i> Artists</Link>
                </li>
                <li className="nav-item mobile-only">
                  <Link to="/profile" className="nav-link"><i className="bi bi-person"></i> Profile</Link>
                </li>
                <li className="nav-item mobile-only">
                  <button onClick={handleSignOut} className="nav-link logout-button">
                    <i className="bi bi-box-arrow-right"></i> Sign Out
                  </button>
                </li>
              </ul>
              <div className="user-menu">
                <div className="user-info">
                  <span className="user-name">
                    {profile?.user_type === 'venue' && profile?.venue_name ? 
                      profile.venue_name : 
                      (profile?.full_name || 
                       (profile?.first_name && profile?.last_name ? 
                        `${profile.first_name} ${profile.last_name}` : 
                        user.email.split('@')[0]))}
                  </span>
                  <Link to="/profile" className="profile-link">
                    <div className="avatar">
                      {profile?.profile_image_url ? (
                        <img 
                          src={profile.profile_image_url} 
                          alt={profile?.full_name || user.email} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'https://via.placeholder.com/40?text=' + (user.email.charAt(0).toUpperCase());
                          }}
                        />
                      ) : (
                        <div className="avatar-placeholder">
                          {profile?.full_name ? profile.full_name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </Link>
                </div>
                <div className="dropdown">
                  <Link to="/profile" className="dropdown-item"><i className="bi bi-person"></i> Profile</Link>
                  <Link to="/settings" className="dropdown-item"><i className="bi bi-gear"></i> Settings</Link>
                  <button onClick={handleSignOut} className="dropdown-item sign-out">
                    <i className="bi bi-box-arrow-right"></i> Sign Out
                  </button>
                </div>
              </div>
            </>
          ) : (
            <ul className="nav-list">
              <li className="nav-item">
                <Link to="/login" className="nav-link">Login</Link>
              </li>
              <li className="nav-item">
                <Link to="/register" className="nav-link">Register</Link>
              </li>
            </ul>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Header;
